// DepthPass2Banner — Gate G3.7 Phase 6 (plan §8.1).
//
// Renders the post-architect-patch re-probe ("pass 2") status as
// a banner across the top of the G3.7 panel. Pass 2 runs
// asynchronously after the architect submits their Approve / Edit
// / Defer / Reject picks; this banner keeps the architect informed
// without blocking the Phase-4 advance.
//
// Four states (plan §8.3 closure-status palette):
//
//   * ``running``               — pass 2 still in flight
//   * ``completed-clean``       — every patched finding closed
//   * ``completed-with-regr``   — at least one regression / unchanged
//   * ``failed``                — LLM 5xx / parse error etc.
//
// Inputs come from the ``DepthFindingsPass2`` envelope plus a
// boolean signaling whether the runner is still in flight (the
// G3.7 panel polls the envelope for ~30 s after submit).

import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  XCircle,
} from 'lucide-react'

import type { DepthFindingsPass2 } from '@/types/depth'

// --------------------------------------------------------------------------- //
// Public props
// --------------------------------------------------------------------------- //
export interface DepthPass2BannerProps {
  /** When true the runner is still in flight (poll has not seen output). */
  running: boolean
  /** Pass-2 envelope once available (null during ``running`` / pre-submit). */
  envelope: DepthFindingsPass2 | null
  /**
   * Free-text error message when the panel detected a fetch failure
   * outside the envelope's ``error`` field (e.g. file 404 after a
   * crash).
   */
  fetchError?: string | null
}

// --------------------------------------------------------------------------- //
// Component
// --------------------------------------------------------------------------- //
export function DepthPass2Banner({
  running,
  envelope,
  fetchError,
}: DepthPass2BannerProps) {
  // No banner before the architect submits their picks — pass 2
  // can't even fire until then. The G3.7 panel suppresses this
  // banner pre-submit by passing ``running=false`` + ``envelope=null``.
  if (!running && !envelope && !fetchError) return null

  if (running && !envelope) {
    return (
      <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 mb-4 flex items-center gap-2 text-sm text-slate-300">
        <Loader2 className="h-4 w-4 animate-spin text-indigo-400 shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold text-slate-200">
            Pass 2 running…
          </p>
          <p className="text-[11.5px] text-slate-400 mt-0.5 leading-snug">
            The probe is re-walking the patched SRD to verify every
            approved patch actually closed its finding. The panel
            will refresh closure badges automatically.
          </p>
        </div>
      </div>
    )
  }

  // Envelope-level error (LLM 5xx, parse failure) — surface as a
  // soft warning. Pass-2 failure does NOT block Phase 4 per plan
  // §11.2; the architect can still advance.
  if (envelope?.error || fetchError) {
    const message = envelope?.error || fetchError
    return (
      <div className="rounded-lg border border-amber-700/40 bg-amber-950/20 p-3 mb-4 flex items-start gap-2 text-sm text-amber-100">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
        <div className="min-w-0">
          <p className="font-semibold">Pass 2 did not complete</p>
          <p className="text-[11.5px] text-amber-200/80 mt-0.5 leading-snug">
            <code className="font-mono text-[11px] text-amber-100">
              {message}
            </code>
            . Closure badges on each finding will stay grey. You can
            still advance to Phase 4 — the Critic will treat absent
            closure data as ``depth_score = 1.0`` per the
            backwards-compat rule.
          </p>
        </div>
      </div>
    )
  }

  if (!envelope) return null

  // Aggregate counts for the rendered status.
  const findings = envelope.findings ?? []
  const closed = findings.filter((f) => f.closure_status === 'closed').length
  const unchanged = findings.filter(
    (f) => f.closure_status === 'unchanged',
  ).length
  const regressed = findings.filter(
    (f) => f.closure_status === 'regressed',
  ).length
  const total = findings.length

  if (regressed > 0 || unchanged > 0) {
    return (
      <div className="rounded-lg border border-amber-700/40 bg-amber-950/20 p-3 mb-4 flex items-start gap-2 text-sm text-amber-100">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
        <div className="min-w-0">
          <p className="font-semibold">
            Pass 2 completed with {regressed > 0 ? 'regressions' : 'gaps'}
          </p>
          <p className="text-[11.5px] text-amber-200/80 mt-0.5 leading-snug">
            {closed} of {total} findings closed; {unchanged} unchanged
            {regressed > 0 ? `, ${regressed} regressed` : ''}. The Critic
            at G4 will surface the unresolved findings as
            <code className="font-mono ml-1 text-amber-100">
              structural_gap
            </code>{' '}
            entries. Re-open the offending finding cards to refine your
            EARS clause, or proceed and let G4 audit.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 mb-4 flex items-start gap-2 text-sm text-emerald-100">
      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-300" />
      <div className="min-w-0">
        <p className="font-semibold">Pass 2 closed clean</p>
        <p className="text-[11.5px] text-emerald-200/80 mt-0.5 leading-snug">
          {closed === total
            ? `All ${total} findings closed by the architect's edits.`
            : `${closed} of ${total} findings closed; the rest were deferred or rejected.`}{' '}
          The Critic at G4 will treat the run's depth_score as healthy.
        </p>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// (Unused) — exported so future callers can reuse the icon palette
// without hard-coding the lucide imports.
// --------------------------------------------------------------------------- //
export const DEPTH_PASS2_ICONS = {
  closed: CheckCircle2,
  unchanged: AlertTriangle,
  regressed: XCircle,
}
